import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, PlayCircle, ShieldCheck } from "lucide-react";

import api, { API_BASE, formatApiErrorDetail, getAuthToken } from "@/lib/api";
import { ModuleTitleTRAINING } from "@/components/training/ModuleTitleTRAINING";

/** Convert a plain YouTube URL to an embed URL. Passes through /embed/ URLs. */
function ytEmbed(url) {
  if (!url) return null;
  if (url.includes("/embed/")) return url;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/))([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return url;
}

const STATUS_LABEL = {
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Completed",
};

export default function TrainingTake() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [training, setTraining] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [attested, setAttested] = useState(false);
  const [watchedConfirmed, setWatchedConfirmed] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/training/assignments/${assignmentId}`);
      setAssignment(data.assignment);
      setTraining(data.training);
      setAttested(!!data.assignment.attested);
      setWatchedConfirmed(data.assignment.status !== "not_started");
      if (data.training.video_source === "upload") {
        setVideoUrl(`${API_BASE}/training/trainings/${data.training.id}/video`);
      } else {
        setVideoUrl(ytEmbed(data.training.video_embed_url));
      }
      if (data.assignment.status === "not_started") {
        api.post(`/training/assignments/${assignmentId}/start`).catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      navigate("/training");
    }
  };

  useEffect(() => {
    load();
     
  }, [assignmentId]);

  const quiz = training?.quiz;
  const quizEnabled = quiz?.enabled && (quiz?.questions || []).length > 0;
  const lastAttempt = (assignment?.quiz_attempts || []).slice(-1)[0];
  const hasPassed = (assignment?.quiz_attempts || []).some((a) => a.passed);

  const canSubmitQuiz = useMemo(() => {
    if (!quizEnabled) return false;
    return (quiz.questions || []).every((_, i) => answers[i] !== undefined);
  }, [answers, quiz, quizEnabled]);

  const submitQuiz = async () => {
    if (!canSubmitQuiz) return;
    setBusy(true);
    try {
      const submission = (quiz.questions || []).map((_, i) => String(answers[i]));
      const { data } = await api.post(`/training/assignments/${assignmentId}/quiz`, {
        answers: submission,
      });
      toast[data.passed ? "success" : "error"](
        data.passed ? `Passed with ${data.score}%.` : `Scored ${data.score}%. You can try again.`,
      );
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!attested) {
      toast.error("Please attest that you completed this training.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/training/assignments/${assignmentId}/complete`, { attested: true });
      toast.success("Completion recorded. Thank you.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!assignment || !training) {
    return (
      <div className="patria-card p-8" data-testid="training-take-loading">
        <p className="text-charcoal-muted italic">Loading training…</p>
      </div>
    );
  }

  const canComplete = (!quizEnabled || hasPassed) && watchedConfirmed && attested && assignment.status !== "completed";
  const isCompleted = assignment.status === "completed";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="training-take-page">
      <Link
        to="/training"
        className="inline-flex items-center text-forest-700 text-sm font-medium"
      >
        <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
        Back to my training
      </Link>

      <div>
        <ModuleTitleTRAINING />
        <div className="mt-3 flex flex-wrap items-baseline gap-3">
          <span className="text-xs uppercase tracking-widest text-charcoal-muted">
            {STATUS_LABEL[assignment.status]}
          </span>
          {training.required && (
            <span className="inline-flex items-center rounded-full border border-terracotta-500/30 bg-terracotta-500/15 text-terracotta-600 px-2 py-0.5 text-xs">
              Required
            </span>
          )}
          {assignment.overdue && !isCompleted && (
            <span className="inline-flex items-center rounded-full bg-terracotta-500 text-cream-100 px-2 py-0.5 text-xs font-medium">
              Overdue
            </span>
          )}
        </div>
        <h1 className="font-serif text-forest-900 text-4xl mt-2">{training.title}</h1>
        <p className="mt-3 text-charcoal-soft max-w-2xl leading-relaxed">
          {training.description}
        </p>
      </div>

      <section className="patria-card p-4 sm:p-6" data-testid="training-video-panel">
        {training.video_source === "embed" && videoUrl && (
          <div className="relative pt-[56.25%] rounded-xl overflow-hidden bg-charcoal">
            <iframe
              title={training.title}
              src={videoUrl}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="training-video-embed"
            />
          </div>
        )}
        {training.video_source === "upload" && (
          <video
            src={`${videoUrl}${getAuthToken() ? `?_t=${Date.now()}` : ""}`}
            controls
            preload="metadata"
            className="w-full rounded-xl bg-charcoal"
            data-testid="training-video-upload"
            onEnded={() => setWatchedConfirmed(true)}
          />
        )}

        {training.video_source === "embed" && !watchedConfirmed && !isCompleted && (
          <label className="mt-4 inline-flex items-center gap-2 text-sm text-charcoal-soft">
            <input
              type="checkbox"
              onChange={(e) => setWatchedConfirmed(e.target.checked)}
              data-testid="training-watched-checkbox"
            />
            I watched this video.
          </label>
        )}
      </section>

      {quizEnabled && !isCompleted && (
        <section className="patria-card p-6" data-testid="training-quiz-panel">
          <h3 className="font-serif text-forest-900 text-xl">Quiz</h3>
          <p className="mt-1 text-xs text-charcoal-muted">
            Passing score: {quiz.passing_score_pct}%. You can retake as many times as you need.
          </p>
          {hasPassed ? (
            <p className="mt-4 inline-flex items-center gap-2 text-forest-800 font-medium">
              <CheckCircle2 className="h-5 w-5 text-forest-600" strokeWidth={1.5} />
              Quiz passed. You&apos;re clear to attest and complete.
            </p>
          ) : (
            <div className="mt-4 space-y-5">
              {(quiz.questions || []).map((q, i) => (
                <div key={q.id || i} className="patria-card p-4" data-testid={`training-quiz-q-${i}`}>
                  <p className="font-medium text-forest-900">
                    {i + 1}. {q.text}
                  </p>
                  <div className="mt-3 space-y-2">
                    {(q.choices || []).map((c, ci) => (
                      <label
                        key={ci}
                        className="flex items-start gap-2 text-sm text-charcoal cursor-pointer"
                      >
                        <input
                          type="radio"
                          name={`q-${i}`}
                          checked={answers[i] === ci}
                          onChange={() => setAnswers({ ...answers, [i]: ci })}
                          data-testid={`training-quiz-q-${i}-c-${ci}`}
                          className="mt-1"
                        />
                        <span>{c.text}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <p className="text-xs text-charcoal-muted">
                  {lastAttempt
                    ? `Last attempt: ${lastAttempt.score}% ${lastAttempt.passed ? "(passed)" : "(did not pass)"}`
                    : "No attempts yet."}
                </p>
                <button
                  onClick={submitQuiz}
                  disabled={!canSubmitQuiz || busy}
                  className="patria-btn-primary"
                  data-testid="training-quiz-submit"
                >
                  {busy ? "Submitting…" : "Submit quiz"}
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {!isCompleted && (
        <section className="patria-card p-6 border-l-4 border-forest-600" data-testid="training-attestation-panel">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-forest-600 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <h3 className="font-serif text-forest-900 text-xl">Attestation</h3>
              <p className="mt-1 text-sm text-charcoal-soft">
                Recording your completion is part of how we honor your time and Patria&apos;s commitments.
              </p>
              <label className="mt-4 flex items-start gap-2 text-sm text-charcoal">
                <input
                  type="checkbox"
                  checked={attested}
                  onChange={(e) => setAttested(e.target.checked)}
                  data-testid="training-attest-checkbox"
                  className="mt-1"
                />
                <span>
                  I certify that I completed this training in good faith and understand
                  its contents.
                </span>
              </label>
              <div className="mt-4">
                <button
                  onClick={complete}
                  disabled={!canComplete || busy}
                  className="patria-btn-primary"
                  data-testid="training-complete-btn"
                >
                  {busy ? "Recording…" : "Mark as complete"}
                </button>
                {!watchedConfirmed && (
                  <p className="mt-2 text-xs text-charcoal-muted">
                    Please confirm you&apos;ve watched the video first.
                  </p>
                )}
                {quizEnabled && !hasPassed && (
                  <p className="mt-2 text-xs text-charcoal-muted">
                    Please pass the quiz to enable completion.
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {isCompleted && (
        <section className="patria-card p-6 bg-forest-600 text-cream-100" data-testid="training-completed-badge">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-6 w-6" strokeWidth={1.5} />
            <div>
              <p className="font-serif text-2xl">Completed.</p>
              <p className="text-sm mt-1 text-cream-100/85">
                Recorded {assignment.completed_at?.split("T")[0] || "—"}.
                {(assignment.quiz_attempts || []).length > 0 &&
                  ` Best quiz score ${assignment.best_score}%.`}
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
